/**
 * 时间消费分配算法
 * 
 * 核心功能：
 * 1. 将调休申请分配到具体的加班时间段
 * 2. 支持年假和调休的混合扣除
 * 3. 生成详细的消费记录用于复制功能
 */

// 时间计算逻辑已移至DataManager中处理

/**
 * 分配调休时间到加班记录
 * 
 * @param {Object} restRequest - 调休申请
 * @param {Array} overtimeRecords - 加班记录列表
 * @param {Object} annualLeave - 年假信息
 * @returns {Object} 分配结果
 */
export function allocateRestTime(restRequest, overtimeRecords, annualLeave) {
  // eslint-disable-next-line no-unused-vars
  const { txbegintime: startTime, txendtime: endTime, id: _restId } = restRequest;
  
  // 计算需要的调休时间（按消费时间计算，不扣除午休）
  const requiredHours = restRequest.duration;
  
  if (requiredHours <= 0) {
    return {
      success: false,
      error: '调休时间计算错误',
      consumptionRecords: []
    };
  }
  
  // 计算可用时间
  const availableOvertimeHours = getTotalAvailableOvertimeHours(overtimeRecords);
  const availableAnnualLeaveHours = annualLeave.remainingHours || 0;
  const totalAvailableHours = availableOvertimeHours + availableAnnualLeaveHours;
  
  if (totalAvailableHours < requiredHours) {
    return {
      success: false,
      error: `可用时间不足。需要${requiredHours}小时，可用${totalAvailableHours}小时`,
      consumptionRecords: []
    };
  }
  
  // 分配策略：优先使用加班时间，不足时使用年假
  const consumptionRecords = [];
  let remainingHours = requiredHours;
  
  // 第一阶段：分配加班时间
  if (remainingHours > 0 && availableOvertimeHours > 0) {
    const overtimeConsumption = allocateFromOvertimeRecords(
      remainingHours,
      overtimeRecords,
      restRequest
    );
    
    consumptionRecords.push(...overtimeConsumption.records);
    remainingHours -= overtimeConsumption.allocatedHours;
  }
  
  // 第二阶段：分配年假时间
  if (remainingHours > 0 && availableAnnualLeaveHours > 0) {
    const annualLeaveConsumption = allocateFromAnnualLeave(
      remainingHours,
      restRequest
    );
    
    consumptionRecords.push(annualLeaveConsumption);
    remainingHours -= annualLeaveConsumption.deductionTime;
  }
  
  return {
    success: true,
    totalAllocatedHours: requiredHours - remainingHours,
    consumptionRecords,
    summary: {
      totalRequiredHours: requiredHours,
      overtimeHoursUsed: consumptionRecords
        .filter(r => r.type === '调休')
        .reduce((sum, r) => sum + r.deductionTime, 0),
      annualLeaveHoursUsed: consumptionRecords
        .filter(r => r.type === '年假')
        .reduce((sum, r) => sum + r.deductionTime, 0)
    }
  };
}

/**
 * 从加班记录中分配时间
 * 
 * @param {number} requiredHours - 需要的小时数
 * @param {Array} overtimeRecords - 加班记录列表
 * @param {Object} restRequest - 调休申请
 * @returns {Object} 分配结果
 */
function allocateFromOvertimeRecords(requiredHours, overtimeRecords, restRequest) {
  const records = [];
  let allocatedHours = 0;
  let remainingHours = requiredHours;
  
  // 按时间顺序排序加班记录（先进先出）
  const sortedRecords = [...overtimeRecords]
    .filter(record => record.remainingTime > 0)
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  
  for (const overtimeRecord of sortedRecords) {
    if (remainingHours <= 0) break;
    
    const availableHours = overtimeRecord.remainingTime;
    const hoursToAllocate = Math.min(remainingHours, availableHours);
    
    // 只有当分配时间大于0.01小时（约36秒）时才创建消费记录，避免创建0小时记录
    if (hoursToAllocate > 0.01) {
      // 计算消费的时间段
      const consumptionTimeRange = calculateConsumptionTimeRange(
        overtimeRecord,
        hoursToAllocate
      );

      const consumptionRecord = {
        id: generateConsumptionId(),
        overtimeId: overtimeRecord.id,
        restStartTime: restRequest.txbegintime,
        restEndTime: restRequest.txendtime,
        consumptionStartTime: consumptionTimeRange.startTime,
        consumptionEndTime: consumptionTimeRange.endTime,
        deductionTime: hoursToAllocate,
        type: '调休'
      };

      records.push(consumptionRecord);
      allocatedHours += hoursToAllocate;
      remainingHours -= hoursToAllocate;
    }
  }
  
  return {
    records,
    allocatedHours
  };
}

/**
 * 从年假中分配时间
 * 
 * @param {number} requiredHours - 需要的小时数
 * @param {Object} restRequest - 调休申请
 * @returns {Object} 年假消费记录
 */
function allocateFromAnnualLeave(requiredHours, restRequest) {
  return {
    id: generateConsumptionId(),
    overtimeId: null,
    restStartTime: restRequest.txbegintime,
    restEndTime: restRequest.txendtime,
    consumptionStartTime: restRequest.txbegintime,
    consumptionEndTime: restRequest.txendtime,
    deductionTime: requiredHours,
    type: '年假'
  };
}

/**
 * 计算消费的时间段
 * 
 * @param {Object} overtimeRecord - 加班记录
 * @param {number} hoursToConsume - 要消费的小时数
 * @returns {Object} 消费时间段
 */
function calculateConsumptionTimeRange(overtimeRecord, hoursToConsume) {
  const startTime = new Date(overtimeRecord.startTime);
  // eslint-disable-next-line no-unused-vars
  const _endTime = new Date(overtimeRecord.endTime);
  const totalHours = overtimeRecord.duration;
  const consumedHours = totalHours - overtimeRecord.remainingTime;
  
  // 计算已消费的时间，从开始时间算起
  const consumedMilliseconds = consumedHours * 60 * 60 * 1000;
  const consumptionStartTime = new Date(startTime.getTime() + consumedMilliseconds);
  
  // 计算本次消费的结束时间
  const consumptionMilliseconds = hoursToConsume * 60 * 60 * 1000;
  const consumptionEndTime = new Date(consumptionStartTime.getTime() + consumptionMilliseconds);
  
  return {
    startTime: consumptionStartTime.toISOString(),
    endTime: consumptionEndTime.toISOString()
  };
}

/**
 * 计算总可用加班时间
 * 
 * @param {Array} overtimeRecords - 加班记录列表
 * @returns {number} 总可用小时数
 */
function getTotalAvailableOvertimeHours(overtimeRecords) {
  return overtimeRecords.reduce((total, record) => {
    return total + (record.remainingTime || 0);
  }, 0);
}

/**
 * 生成消费记录ID
 *
 * @returns {string} 唯一ID
 */
function generateConsumptionId() {
  // 使用更高精度的时间戳和更长的随机字符串确保唯一性
  return Date.now().toString() + '-' + Math.random().toString(36).substr(2, 12) + '-' + Math.random().toString(36).substr(2, 6);
}

/**
 * 更新加班记录的剩余时间
 * 
 * @param {Array} overtimeRecords - 加班记录列表
 * @param {Array} consumptionRecords - 消费记录列表
 * @returns {Array} 更新后的加班记录列表
 */
export function updateOvertimeRecordsRemainingTime(overtimeRecords, consumptionRecords) {
  const updatedRecords = overtimeRecords.map(record => ({ ...record }));
  
  // 计算每个加班记录的总消费时间
  const consumptionByOvertimeId = {};
  
  consumptionRecords.forEach(consumption => {
    if (consumption.overtimeId && consumption.type === '调休') {
      if (!consumptionByOvertimeId[consumption.overtimeId]) {
        consumptionByOvertimeId[consumption.overtimeId] = 0;
      }
      consumptionByOvertimeId[consumption.overtimeId] += consumption.deductionTime;
    }
  });
  
  // 更新剩余时间
  updatedRecords.forEach(record => {
    const totalConsumed = consumptionByOvertimeId[record.id] || 0;
    record.remainingTime = Math.max(0, record.duration - totalConsumed);
  });
  
  return updatedRecords;
}

/**
 * 验证消费分配的一致性
 * 
 * @param {Array} overtimeRecords - 加班记录列表
 * @param {Array} consumptionRecords - 消费记录列表
 * @param {Array} restRecords - 调休记录列表
 * @returns {Object} 验证结果
 */
export function validateConsumptionConsistency(overtimeRecords, consumptionRecords, restRecords) {
  const errors = [];
  const warnings = [];
  
  // 检查消费记录是否超出加班记录的总时间
  const consumptionByOvertimeId = {};
  consumptionRecords.forEach(consumption => {
    if (consumption.overtimeId && consumption.type === '调休') {
      if (!consumptionByOvertimeId[consumption.overtimeId]) {
        consumptionByOvertimeId[consumption.overtimeId] = 0;
      }
      consumptionByOvertimeId[consumption.overtimeId] += consumption.deductionTime;
    }
  });
  
  overtimeRecords.forEach(record => {
    const totalConsumed = consumptionByOvertimeId[record.id] || 0;
    if (totalConsumed > record.duration) {
      errors.push(`加班记录 ${record.id} 的消费时间(${totalConsumed}h)超出总时间(${record.duration}h)`);
    }
  });
  
  // 检查调休记录与消费记录的对应关系
  restRecords.forEach(restRecord => {
    const relatedConsumptions = consumptionRecords.filter(c => 
      c.restStartTime === restRecord.txbegintime && 
      c.restEndTime === restRecord.txendtime
    );
    
    if (relatedConsumptions.length === 0) {
      warnings.push(`调休记录 ${restRecord.id} 没有对应的消费记录`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}
